import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ProductCard from "../../../components/controls/ProductCard";
import CartSidebar from "./CartSidebar";
import { useCart } from "../../../contexts/CartContext";
import {
  ChevronRightIcon,
  MapPinIcon,
  CalendarIcon,
  TagIcon,
} from "@heroicons/react/24/solid";
import { fetchAvailableItems } from "../../../db/products";
import AddToCartModal from "./AddtoCartModal";

const ProductServiceSelection = ({
  nextStep,
  updateEventData,
  eventData,
  returnToCategorySelection,
}) => {
  const { t, i18n } = useTranslation();
  const { cart, handleAddToCart } = useCart();
  const [showModal, setShowModal] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const lang = i18n.language;
  const {
    location = null,
    selectedProducts = [],
    selectedServices = [],
    eventDate = null,
    eventEndDate = null,
    guestArrivalTime = null,
  } = eventData || {};

  const dates = {
    eventDate,
    eventEndDate,
    guestArrivalTime,
  };

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const actionTriggered = useRef(false);

  const allCategories = [
    ...new Set([...selectedProducts, ...selectedServices]),
  ];
  const currentCategory = allCategories[currentCategoryIndex];

  const getCurrentCategoryItems = () =>
    allItems.filter((item) => item.categoryId === currentCategory);

  useEffect(() => {
    const loadItems = async () => {
      if (allCategories.length === 0) return;

      try {
        setLoading(true);
        setError(null);
        actionTriggered.current = false;

        const userLatitude = location?.latitude || null;
        const userLongitude = location?.longitude || null;
        const data = await fetchAvailableItems(
          userLatitude,
          userLongitude,
          allCategories,
          dates
        );
        setAllItems(data);
        if (!data.length && !actionTriggered.current) {
          actionTriggered.current = true;
          returnToCategorySelection();
          return;
        }
        const currentItems = data.filter(
          (item) => item.categoryId === currentCategory
        );

        if (currentItems.length === 0 && !actionTriggered.current) {
          actionTriggered.current = true;
          handleNextCategory();
        }
      } catch (err) {
        setError(t("errors.fetchItems"));
        console.error("Error loading items:", err);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, []);

  useEffect(() => {
    if (allItems.length > 0 && currentCategory) {
      const currentItems = allItems.filter(
        (item) => item.categoryId === currentCategory
      );

      if (currentItems.length === 0) {
        setError(t("errors.noItemsFound"));
      } else {
        setError(null);
      }
    }
  }, [currentCategory, allItems, t]);

  const addItemToCart = (item) => {
    handleAddToCart(item);
  };

  const handleNextCategory = () => {
    if (currentCategoryIndex < allCategories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
    } else {
      nextStep();
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold">
            {t("productServiceSelection.title")}
          </h2>
          <div className="flex gap-2 mt-2">
            <p className="text-gray-600 py-1">
              {t("productServiceSelection.filtersApplied")}:
            </p>
            {currentCategory && (
              <span className="flex items-center gap-2 bg-primary text-white px-4 py-1 rounded-full">
                <TagIcon className="w-5 h-5" />
                {currentCategory}
              </span>
            )}
            {location && (
              <span className="flex items-center gap-2 bg-primary text-white px-4 py-1 rounded-full">
                <MapPinIcon className="w-5 h-5" />
                {location.formattedAddress}
              </span>
            )}
            {dates[0]?.date && (
              <span className="flex items-center gap-2 bg-primary text-white px-4 py-1 rounded-full">
                <CalendarIcon className="w-5 h-5" />
                {dates[0]?.date} {dates[0]?.time}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-600">{t("loading")}</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {getCurrentCategoryItems().map((item) => (
              <ProductCard
                key={item.id}
                product={item}
                onAddToCart={() => setModalProduct(item)}
                lang={lang}
                showAddToCartButton
              />
            ))}
          </div>
        )}
      </div>

      <CartSidebar
        cart={cart}
        budget={eventData.budget}
        lang={lang}
        limitWidth={true}
      />

      <button
        onClick={handleNextCategory}
        className="fixed bottom-8 right-8 bg-primary text-white py-5 px-8 rounded-full shadow-3xl hover:bg-primary/90 transition-all flex items-center gap-2 animate-scaleUpDown"
      >
        {currentCategoryIndex < allCategories.length - 1
          ? t("productServiceSelection.nextCategory")
          : t("productServiceSelection.finishSelection")}
        <ChevronRightIcon className="w-5 h-5" />
      </button>
      {modalProduct && (
        <AddToCartModal
          product={modalProduct}
          onClose={() => setModalProduct(null)}
          eventDates={{
            eventDate: eventData.eventDate,
            eventEndDate: eventData.eventEndDate,
          }}
        />
      )}
    </div>
  );
};

export default ProductServiceSelection;
